import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Outlet, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { traderService, Trader, TraderFeatures } from '../../services/traderService';
import { triggerKeywordGeneration } from '../../services/keywordService';
import { Phone, Globe, MapPin, Info, Edit2, Check, X, Upload, Store, User, RefreshCw } from 'lucide-react';
import { supabase } from '../../config/supabase';
import SocialLinksCard from '../../components/dashboard/SocialLinksCard';
import FeatureShowcase from '../../components/FeatureShowcase';
import { compressImage, AVATAR_PRESET, GALLERY_PRESET, formatFileSize } from '../../utils/imageCompression';

const AvatarUpload = ({ 
  trader, 
  onAvatarUpdate 
}: { 
  trader: Trader, 
  onAvatarUpdate: (url: string) => void 
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isHovered, setIsHovered] = useState(false);

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      // Validate file type and size
      const validTypes = ['image/jpeg', 'image/png', 'image/gif'];
      const maxSize = 5 * 1024 * 1024; // 5MB

      if (!validTypes.includes(file.type)) {
        alert('Invalid file type. Please upload a JPEG, PNG, or GIF.');
        return;
      }

      if (file.size > maxSize) {
        alert('File is too large. Maximum size is 5MB.');
        return;
      }
      const originalSize = file.size;
      const compressedFile = await compressImage(file, AVATAR_PRESET);
      const compressedSize = compressedFile.size;
      const reduction = originalSize > 0
        ? ((originalSize - compressedSize) / originalSize * 100).toFixed(0)
        : '0';

      // Upload avatar (compressed)
      const avatarUrl = await traderService.uploadAvatar(trader.id, compressedFile);
      
      // Update trader with new avatar
      await traderService.updateTrader(trader.id, { avatar_url: avatarUrl });
      
      // Call callback to update UI
      onAvatarUpdate(avatarUrl);
      
      alert(
        `Avatar updated successfully. Optimized: ${formatFileSize(originalSize)} → ${formatFileSize(compressedSize)} (${reduction}% smaller)`
      );
    } catch (error) {
      console.error('Error uploading avatar:', error);
      alert('Failed to upload avatar');
    }
  };

  return (
    <>
      <input 
        type="file" 
        ref={fileInputRef} 
        className="hidden" 
        accept="image/jpeg,image/png,image/gif"
        onChange={handleFileChange}
      />
      <div 
        className="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <button 
          onClick={() => fileInputRef.current?.click()}
          className="text-white flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 px-3 py-2 rounded-full"
        >
          <Upload className="w-5 h-5" />
          <span className="text-sm">Change Avatar</span>
        </button>
      </div>
    </>
  );
};

const EditableInput = ({ 
  value, 
  onSave, 
  onCancel,
  type = 'text',
  placeholder = '',
  isTextarea = false
}: { 
  value: string, 
  onSave: (newValue: string) => void, 
  onCancel: () => void,
  type?: string,
  placeholder?: string,
  isTextarea?: boolean
}) => {
  const [inputValue, setInputValue] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (isTextarea) {
      textareaRef.current?.focus();
    } else {
      inputRef.current?.focus();
    }
  }, [isTextarea]);

  const handleSave = () => {
    if (inputValue.trim() !== '') {
      onSave(inputValue.trim());
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    // For textarea, only save on Ctrl+Enter or Cmd+Enter
    if (isTextarea) {
      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
        handleSave();
      } else if (e.key === 'Escape') {
        onCancel();
      }
    } else {
      // For input, save on Enter
      if (e.key === 'Enter') {
        handleSave();
      } else if (e.key === 'Escape') {
        onCancel();
      }
    }
  };

  return (
    <div className="flex items-start space-x-2 w-full">
      {isTextarea ? (
        <textarea
          ref={textareaRef}
          value={inputValue}
          placeholder={placeholder}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          className="flex-grow px-2 py-1 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[80px]"
          rows={3}
        />
      ) : (
        <input
          ref={inputRef}
          type={type}
          value={inputValue}
          placeholder={placeholder}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          className="flex-grow px-2 py-1 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      )}
      <button 
        onClick={handleSave}
        className="text-green-500 hover:bg-green-100 p-1 rounded-full mt-1"
        title="Save"
      >
        <Check className="w-5 h-5" />
      </button>
      <button 
        onClick={onCancel}
        className="text-red-500 hover:bg-red-100 p-1 rounded-full mt-1"
        title="Cancel"
      >
        <X className="w-5 h-5" />
      </button>
    </div>
  );
};

const EditableField = ({ 
  label, 
  value, 
  icon: Icon, 
  onEdit,
  isEditing,
  onSave,
  onCancel,
  type = 'text',
  placeholder = '',
  isTextarea = false
}: { 
  label: string, 
  value: string, 
  icon: React.ComponentType<{className?: string}>, 
  onEdit: () => void,
  isEditing?: boolean,
  onSave?: (newValue: string) => void,
  onCancel?: () => void,
  type?: string,
  placeholder?: string,
  isTextarea?: boolean
}) => {
  const [isHovered, setIsHovered] = useState(false);

  if (isEditing) {
    return (
      <div className="flex items-center text-gray-600 group relative w-full">
        <Icon className="w-5 h-5 mr-2 text-blue-600" />
        <EditableInput 
          value={value}
          onSave={onSave!}
          onCancel={onCancel!}
          type={type}
          placeholder={placeholder}
          isTextarea={isTextarea}
        />
      </div>
    );
  }

  return (
    <div 
      className="flex items-center text-gray-600 group relative w-full"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Icon className="w-5 h-5 mr-2 text-blue-600" />
      <span className="flex-grow">{value}</span>
      <button 
        onClick={onEdit}
        className={`ml-2 p-1 rounded-full 
          ${isHovered ? 'bg-blue-100 text-blue-600' : 'text-gray-400'}
          hover:bg-blue-100 hover:text-blue-600
          transition-all duration-200 ease-in-out
          absolute right-0 top-1/2 -translate-y-1/2
        `}
        title={`Edit ${label}`}
      >
        <Edit2 className="w-4 h-4" />
      </button>
    </div>
  );
};

const TraderDashboard = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [trader, setTrader] = useState<Trader | null>(null);
  const [loading, setLoading] = useState(true);
  const [editingField, setEditingField] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [debugInfo, setDebugInfo] = useState<any>({});
  const [traderFeatures, setTraderFeatures] = useState<TraderFeatures | null>(null);
  const [isRegeneratingKeywords, setIsRegeneratingKeywords] = useState(false);

  const fetchTraderDetails = useCallback(async () => {
    console.log('Fetch trader details - Start', { 
      authLoading, 
      userExists: !!user,
      userId: user?.id 
    });

    // If auth is still loading, don't attempt to fetch trader
    if (authLoading) {
      console.log('Auth still loading, waiting...');
      return;
    }

    // If no user, redirect to login or registration
    if (!user) {
      console.warn('No user found, redirecting to login');
      navigate('/login');
      setLoading(false);
      return;
    }

    try {
      console.log(`Attempting to fetch trader for user ID: ${user.id}`);
      
      // Fetch user's session to ensure we have the latest information
      const { data: { session } } = await supabase.auth.getSession();
      console.log('Current session:', session);

      const traderData = await traderService.getTraderByUserId(user.id);
      
      console.log('Trader data fetched:', traderData);
      
      // Detailed debug information
      setDebugInfo({
        userEmail: user.email,
        userId: user.id,
        traderDataReceived: !!traderData,
        traderDetails: traderData
      });

      if (!traderData) {
        console.warn('No trader data found for user');
        setError('No trader data found');
        navigate('/trader/register');
        setLoading(false);
        return;
      }

      setTrader(traderData);
      setError(null);
    } catch (error) {
      console.error('Error fetching trader details:', error);
      setError('Failed to fetch trader details');
      
      // Log detailed error information
      setDebugInfo(prev => ({
        ...prev,
        errorDetails: error instanceof Error ? {
          message: error.message,
          name: error.name,
          stack: error.stack
        } : String(error)
      }));

      navigate('/trader/register');
    } finally {
      setLoading(false);
    }
  }, [user, authLoading, navigate]);

  // Trigger fetch when auth state changes
  useEffect(() => {
    console.log('TraderDashboard useEffect triggered', { 
      authLoading, 
      userExists: !!user 
    });
    
    // Only fetch if auth loading is complete
    if (!authLoading) {
      fetchTraderDetails();
    }
  }, [fetchTraderDetails, authLoading]);

  // Force render if trader exists
  useEffect(() => {
    if (trader) {
      console.log('Trader data confirmed, forcing re-render');
      setLoading(false);
    }
  }, [trader]);

  const fetchTraderFeatures = useCallback(async () => {
    // Prefer user.id over trader.user_id
    const userId = user?.id || trader?.user_id;
    
    if (!userId) {
      console.error('No user ID found in trader or user data');
      return;
    }

    console.log('Fetching trader features for user_id:', userId);
    
    try {
      const features = await traderService.getTraderFeatures(userId);
      console.log('Fetched trader features:', features);
      
      // If no features found, create default features
      if (!features) {
        const defaultFeatures: TraderFeatures = {
          user_id: userId,
          feature1_enabled: true,
          feature1_title: trader?.store_name || 'My Business',
          feature1_description: trader?.description || 'Discover our unique offerings',
          feature1_image: trader?.avatar_url || '/bally-Market-sign-200px.png',
          feature2_enabled: false,
          feature2_title: 'Orders',
          feature2_description: 'View and manage your orders',
          feature2_image: '/bally-Market-sign-200px.png',
          feature3_enabled: false,
          feature3_title: 'Analytics',
          feature3_description: 'View your sales and performance analytics',
          feature3_image: '/bally-Market-sign-200px.png'
        };
        setTraderFeatures(defaultFeatures);
      } else {
        setTraderFeatures(features);
      }
    } catch (error) {
      console.error('Error fetching trader features:', error);
      setTraderFeatures(null);
    }
  }, [user?.id, trader?.user_id, trader?.store_name, trader?.description, trader?.avatar_url]);

  useEffect(() => {
    if (trader?.user_id) {
      console.log('Trader data confirmed, fetching features:', trader.user_id);
      fetchTraderFeatures();
    } else {
      console.log('No trader user_id available yet');
    }
  }, [trader, fetchTraderFeatures]);

  const handleEditField = (field: string) => {
    setEditingField(field);
  };

  const handleSaveField = async (field: string, value: string) => {
    if (!trader) {
      console.error('No trader data to update');
      return;
    }

    try {
      const updatedTrader = await traderService.updateTrader(trader.id, { [field]: value });
      if (updatedTrader) {
        setTrader(updatedTrader);
        setEditingField(null);
      } else {
        console.error(`Failed to update ${field}: No data returned`);
        alert(`Failed to update ${field}`);
      }
    } catch (error) {
      console.error(`Error updating ${field}:`, error);
      alert(`Failed to update ${field}`);
    }
  };

  const handleCancelEdit = () => {
    setEditingField(null);
  };

  const handleAvatarUpdate = (newAvatarUrl: string) => {
    if (trader) {
      setTrader({ ...trader, avatar_url: newAvatarUrl });
    }
  };

  const handleToggleMapVisibility = async () => {
    if (!trader) {
      console.error('No trader data to update map visibility');
      return;
    }

    const current = (trader as any).show_map ?? true;
    const next = !current;

    try {
      const updatedTrader = await traderService.updateTrader(trader.id, { show_map: next } as any);
      if (updatedTrader) {
        setTrader(updatedTrader);
        alert(`Map visibility ${next ? 'enabled' : 'disabled'} on your public profile.`);
      } else {
        console.error('Failed to update map visibility: No data returned');
        alert('Failed to update map visibility');
      }
    } catch (error) {
      console.error('Error updating map visibility:', error);
      alert('Failed to update map visibility');
    }
  };

  // State for editing features
  const [editingFeature, setEditingFeature] = useState<{
    key: string;
    title: string;
    description: string;
    enabled: boolean;
    image: string;
  } | null>(null);

  // State for image upload
  const [imageFile, setImageFile] = useState<File | null>(null);

  // Feature section meta state
  const [featureSectionTitle, setFeatureSectionTitle] = useState('Featured Offerings');
  const [featureLayout, setFeatureLayout] = useState<'rows' | 'columns'>('rows');
  const [isSavingFeatureMeta, setIsSavingFeatureMeta] = useState(false);
  const [featureMetaError, setFeatureMetaError] = useState<string | null>(null);

  // Handle feature edit initiation
  const handleEditFeature = (feature: {
    key: keyof TraderFeatures;
    titleKey: keyof TraderFeatures;
    descKey: keyof TraderFeatures;
    enabledKey: keyof TraderFeatures;
    imageKey: keyof TraderFeatures;
    defaultTitle: string;
    defaultDesc: string;
    defaultImage: string;
  }) => {
    if (!traderFeatures) return;

    console.log('Editing feature:', feature);

    // Prepare editing feature state
    setEditingFeature({
      key: feature.key,
      title: (traderFeatures[feature.titleKey as keyof TraderFeatures] as string) 
        || feature.defaultTitle,
      description: (traderFeatures[feature.descKey as keyof TraderFeatures] as string) 
        || feature.defaultDesc,
      image: (traderFeatures[feature.imageKey as keyof TraderFeatures] as string) 
        || feature.defaultImage,
      enabled: !!localFeatureToggles[feature.enabledKey as keyof typeof localFeatureToggles]
    });
  };

  // Handle feature update
  const handleUpdateFeature = async () => {
    if (!editingFeature || !traderFeatures) return;

    try {
      // Prepare update data
      const updateData: Partial<TraderFeatures> = {
        [`${editingFeature.key}_title`]: editingFeature.title,
        [`${editingFeature.key}_description`]: editingFeature.description,
        [`${editingFeature.key}_enabled`]: editingFeature.enabled
      };

      // Handle image upload if a new file is selected
      if (imageFile) {
        try {
          const originalSize = imageFile.size;
          const compressedFile = await compressImage(imageFile, GALLERY_PRESET);
          const compressedSize = compressedFile.size;
          const reduction = originalSize > 0
            ? ((originalSize - compressedSize) / originalSize * 100).toFixed(0)
            : '0';

          const uploadPath = `trader_features/${traderFeatures.user_id}/${editingFeature.key}_${Date.now()}.jpg`;
          
          // Upload image to Supabase storage
          const { data: uploadData, error: uploadError } = await supabase.storage
            .from('avatars')
            .upload(uploadPath, compressedFile, {
              cacheControl: '3600',
              upsert: true
            });

          if (uploadError) {
            console.error('Image upload error:', uploadError);
            alert('Failed to upload image');
            return;
          }

          // Get public URL for the uploaded image
          const { data: urlData } = supabase.storage
            .from('avatars')
            .getPublicUrl(uploadPath);

          if (urlData) {
            updateData[`${editingFeature.key}_image`] = urlData.publicUrl;
            console.log('Image uploaded successfully:', urlData.publicUrl);
            alert(
              `Feature image optimized: ${formatFileSize(originalSize)} → ${formatFileSize(compressedSize)} (${reduction}% smaller)`
            );
          }
        } catch (uploadError) {
          console.error('Unexpected image upload error:', uploadError);
          alert('Failed to upload image');
          return;
        }
      }

      const updatedFeatures = await traderService.updateTraderFeatures(
        traderFeatures.user_id, 
        updateData
      );

      if (updatedFeatures) {
        // Update trader features state
        setTraderFeatures(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            [`${editingFeature.key}_title`]: editingFeature.title,
            [`${editingFeature.key}_description`]: editingFeature.description,
            [`${editingFeature.key}_enabled`]: editingFeature.enabled,
            [`${editingFeature.key}_image`]: updateData[`${editingFeature.key}_image`] || prev[`${editingFeature.key}_image`],
            updated_at: new Date().toISOString()
          };
        });

        // Update local toggles to match
        setLocalFeatureToggles(prev => ({
          ...prev,
          [`${editingFeature.key}_enabled`]: editingFeature.enabled
        }));

        // Reset image file state
        setImageFile(null);

        // Close edit mode
        setEditingFeature(null);

        console.log('Feature updated successfully', updatedFeatures);
      } else {
        console.error('Failed to update feature: No data returned');
        alert('Failed to update feature');
      }
    } catch (error) {
      console.error('Unexpected error updating feature:', error);
      alert('An unexpected error occurred');
    }
  };

  // State for feature toggles
  const [localFeatureToggles, setLocalFeatureToggles] = useState<{
    feature1_enabled: boolean;
    feature2_enabled: boolean;
    feature3_enabled: boolean;
  }>({
    feature1_enabled: traderFeatures?.feature1_enabled || false,
    feature2_enabled: traderFeatures?.feature2_enabled || false,
    feature3_enabled: traderFeatures?.feature3_enabled || false
  });

  // Initialize local feature toggles from trader features
  useEffect(() => {
    const initializeFeatureToggles = () => {
      if (!traderFeatures) {
        console.log('No trader features available for initialization');
        return;
      }

      console.log('Initializing local feature toggles from trader features:', traderFeatures);

      // Explicitly map feature toggles to ensure correct boolean conversion
      const initialToggles = {
        feature1_enabled: !!traderFeatures.feature1_enabled,
        feature2_enabled: !!traderFeatures.feature2_enabled,
        feature3_enabled: !!traderFeatures.feature3_enabled
      };

      console.log('Computed initial toggles:', initialToggles);

      // Only update if there's a change to prevent unnecessary re-renders
      setLocalFeatureToggles(prev => {
        const shouldUpdate = 
          prev.feature1_enabled !== initialToggles.feature1_enabled ||
          prev.feature2_enabled !== initialToggles.feature2_enabled ||
          prev.feature3_enabled !== initialToggles.feature3_enabled;

        if (shouldUpdate) {
          console.log('Updating local feature toggles', initialToggles);
          return initialToggles;
        }
        return prev;
      });
    };

    // Run initialization
    initializeFeatureToggles();
  }, [traderFeatures]);

  // Keep local meta state synced with database
  useEffect(() => {
    if (!traderFeatures) {
      return;
    }

    setFeatureSectionTitle(traderFeatures.features_section_title || 'Featured Offerings');
    setFeatureLayout(traderFeatures.features_layout === 'columns' ? 'columns' : 'rows');
  }, [traderFeatures]);

  const handleSaveFeatureSectionTitle = async () => {
    if (!traderFeatures) {
      return;
    }

    try {
      setIsSavingFeatureMeta(true);
      setFeatureMetaError(null);

      const updated = await traderService.updateTraderFeatures(traderFeatures.user_id, {
        features_section_title: featureSectionTitle
      });

      if (updated) {
        setTraderFeatures(prev => (prev ? { ...prev, features_section_title: updated.features_section_title } : prev));
        setFeatureSectionTitle(updated.features_section_title || 'Featured Offerings');
      }
    } catch (error) {
      console.error('Error updating feature section title:', error);
      setFeatureMetaError('Failed to update feature section title.');
    } finally {
      setIsSavingFeatureMeta(false);
    }
  };

  const handleFeatureLayoutChange = async (nextLayout: 'rows' | 'columns') => {
    if (!traderFeatures || featureLayout === nextLayout) {
      return;
    }

    try {
      setIsSavingFeatureMeta(true);
      setFeatureMetaError(null);

      const updated = await traderService.updateTraderFeatures(traderFeatures.user_id, {
        features_layout: nextLayout
      });

      if (updated) {
        setTraderFeatures(prev => (prev ? { ...prev, features_layout: updated.features_layout } : prev));
        setFeatureLayout(updated.features_layout === 'columns' ? 'columns' : 'rows');
      }
    } catch (error) {
      console.error('Error updating feature layout:', error);
      setFeatureMetaError('Failed to update feature layout.');
    } finally {
      setIsSavingFeatureMeta(false);
    }
  };

  // Function to update feature status in database
  const updateFeatureStatus = async (featureKey: string, newStatus: boolean) => {
    if (!traderFeatures || !trader) {
      console.error('Cannot update feature status: Missing trader features or trader data');
      return;
    }

    console.log('Updating feature status:', { 
      featureKey, 
      newStatus, 
      currentTraderFeatures: traderFeatures 
    });

    try {
      // Prepare update data
      const updateData: Partial<TraderFeatures> = {
        [`${featureKey}_enabled`]: newStatus
      };

      // Immediately update local state for responsive UI
      setLocalFeatureToggles(prev => ({
        ...prev,
        [`${featureKey}_enabled`]: newStatus
      }));

      // Update in database
      const updatedFeatures = await traderService.updateTraderFeatures(
        traderFeatures.user_id, 
        updateData
      );

      console.log('Feature status update response:', updatedFeatures);

      if (updatedFeatures) {
        // Update trader features state
        setTraderFeatures(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            [`${featureKey}_enabled`]: newStatus,
            updated_at: new Date().toISOString()
          };
        });

        console.log('Feature status updated successfully');
      } else {
        console.error('Failed to update feature status in database');
        
        // Revert local state if database update fails
        setLocalFeatureToggles(prev => ({
          ...prev,
          [`${featureKey}_enabled`]: !newStatus
        }));

        alert('Failed to update feature status');
      }
    } catch (error) {
      console.error('Unexpected error updating feature status:', error);
      
      // Revert local state on error
      setLocalFeatureToggles(prev => ({
        ...prev,
        [`${featureKey}_enabled`]: !newStatus
      }));

      // More detailed error handling
      if (error instanceof Error) {
        alert(`Failed to update feature status: ${error.message}`);
      } else {
        alert('An unexpected error occurred');
      }
    }
  };

  // Render trader features
  const renderTraderFeatures = () => {
    console.log('Rendering trader features:', JSON.stringify(traderFeatures, null, 2));
    console.log('Local feature toggles:', JSON.stringify(localFeatureToggles, null, 2));
    
    if (!traderFeatures) {
      console.log('No trader features to render');
      return null;
    }

    const featuresToRender = [
      { 
        key: 'feature1', 
        label: 'Feature 1',
        enabledKey: 'feature1_enabled', 
        titleKey: 'feature1_title', 
        descKey: 'feature1_description', 
        imageKey: 'feature1_image',
        defaultTitle: trader?.store_name || 'My Business',
        defaultDesc: trader?.description || 'Discover our unique offerings',
        defaultImage: trader?.avatar_url || '/bally-Market-sign-200px.png'
      },
      { 
        key: 'feature2', 
        label: 'Feature 2',
        enabledKey: 'feature2_enabled', 
        titleKey: 'feature2_title', 
        descKey: 'feature2_description', 
        imageKey: 'feature2_image',
        defaultTitle: 'Orders',
        defaultDesc: 'View and manage your orders',
        defaultImage: '/bally-Market-sign-200px.png'
      },
      { 
        key: 'feature3', 
        label: 'Feature 3',
        enabledKey: 'feature3_enabled', 
        titleKey: 'feature3_title', 
        descKey: 'feature3_description', 
        imageKey: 'feature3_image',
        defaultTitle: 'Analytics',
        defaultDesc: 'View your sales and performance analytics',
        defaultImage: '/bally-Market-sign-200px.png'
      }
    ];

    const normalizedFeatureData = featuresToRender.map(feature => {
      const isEnabled = !!localFeatureToggles[feature.enabledKey as keyof typeof localFeatureToggles];
      const title = (traderFeatures[feature.titleKey as keyof TraderFeatures] as string) 
        || feature.defaultTitle;
      const description = (traderFeatures[feature.descKey as keyof TraderFeatures] as string) 
        || feature.defaultDesc;
      const imageUrl = (traderFeatures[feature.imageKey as keyof TraderFeatures] as string) 
        || feature.defaultImage;

      return {
        feature,
        isEnabled,
        title,
        description,
        imageUrl
      };
    });

    const previewFeatures = normalizedFeatureData
      .filter(item => item.isEnabled)
      .map(item => ({ title: item.title, description: item.description, image: item.imageUrl }));

    return (
      <>
        <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-4 mb-6">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              Features section title
            </label>
            <div className="flex flex-col sm:flex-row gap-3">
              <input
                type="text"
                value={featureSectionTitle}
                onChange={(e) => setFeatureSectionTitle(e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Featured Offerings"
              />
              <button
                type="button"
                onClick={handleSaveFeatureSectionTitle}
                disabled={isSavingFeatureMeta || !featureSectionTitle.trim()}
                className="px-4 py-2 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSavingFeatureMeta ? 'Saving...' : 'Save title'}
              </button>
            </div>
          </div>

          <div>
            <p className="text-sm font-semibold text-gray-700 mb-2">Desktop layout</p>
            <div className="inline-flex rounded-md border border-gray-200 overflow-hidden">
              {(['rows', 'columns'] as Array<'rows' | 'columns'>).map(layoutOption => (
                <button
                  key={layoutOption}
                  type="button"
                  onClick={() => handleFeatureLayoutChange(layoutOption)}
                  className={`px-4 py-2 text-sm font-medium transition-colors ${
                    featureLayout === layoutOption
                      ? 'bg-blue-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  } ${layoutOption === 'rows' ? 'border-r border-gray-200' : ''}`}
                >
                  {layoutOption === 'rows' ? 'Rows (default)' : 'Columns'}
                </button>
              ))}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Rows alternate image/description for each feature. Columns show individual cards.
            </p>
          </div>

          {featureMetaError && (
            <p className="text-sm text-red-500">{featureMetaError}</p>
          )}
        </div>

        <div className="hidden sm:grid sm:grid-cols-3 gap-4 mb-4">
          {normalizedFeatureData.map(({ feature, isEnabled }) => {
            console.log(`Feature ${feature.key} enabled state:`, {
              databaseValue: traderFeatures[feature.enabledKey as keyof TraderFeatures],
              localToggleValue: isEnabled
            });
            
            return (
              <div key={feature.key} className="flex items-center space-x-2 justify-between sm:justify-start">
                <span className="text-sm font-semibold text-gray-600">
                  {feature.label}
                </span>
                <input 
                  type="checkbox"
                  checked={isEnabled}
                  onChange={() => {
                    // Immediately update local state for responsive UI
                    const newEnabledState = !isEnabled;
                    updateFeatureStatus(feature.key, newEnabledState);
                  }}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <button 
                  onClick={() => handleEditFeature(feature)}
                  className="text-gray-500 hover:text-blue-600"
                  title="Edit Feature"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                  </svg>
                </button>
              </div>
            );
          })}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {normalizedFeatureData.map(({ feature, isEnabled, title, description, imageUrl }) => {
            const isEditing = editingFeature?.key === feature.key;

            return (
              <div 
                key={feature.key} 
                className={`bg-blue-50 p-6 rounded-lg relative ${!isEnabled ? 'opacity-50' : ''}`}
              >
                {/* Mobile-specific toggle with clear association to each feature card */}
                <div className="sm:hidden flex items-center justify-between mb-4">
                  <span className="text-sm font-semibold text-gray-600">
                    {feature.label}
                  </span>
                  <input
                    type="checkbox"
                    checked={isEnabled}
                    onChange={() => {
                      const newEnabledState = !isEnabled;
                      updateFeatureStatus(feature.key, newEnabledState);
                    }}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                </div>

                {isEditing && editingFeature ? (
                  <div className="space-y-4">
                    <div className="flex justify-center mb-4">
                      {/* Image preview */}
                      <img 
                        src={
                          imageFile 
                            ? URL.createObjectURL(imageFile) 
                            : editingFeature.image || feature.defaultImage
                        }
                        alt={`${editingFeature.title} Icon`} 
                        className="h-32 w-32 object-contain rounded-lg"
                        onError={(e) => {
                          const imgEl = e.target as HTMLImageElement;
                          imgEl.src = feature.defaultImage;
                          console.log(`Error loading image for ${editingFeature.title}, using fallback`);
                        }}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Feature Title
                      </label>
                      <input 
                        type="text"
                        value={editingFeature.title}
                        onChange={(e) => setEditingFeature(prev => 
                          prev ? {...prev, title: e.target.value} : null
                        )}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Feature Description
                      </label>
                      <textarea 
                        value={editingFeature.description}
                        onChange={(e) => setEditingFeature(prev => 
                          prev ? {...prev, description: e.target.value} : null
                        )}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                        rows={3}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Feature Image
                      </label>
                      <input 
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            // Validate file size (max 5MB)
                            if (file.size > 5 * 1024 * 1024) {
                              alert('File is too large. Maximum size is 5MB.');
                              e.target.value = ''; // Clear the input
                              return;
                            }
                            setImageFile(file);
                          }
                        }}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                      />
                    </div>
                    <div className="flex justify-between mt-4">
                      <button 
                        onClick={handleUpdateFeature}
                        className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600"
                      >
                        Save
                      </button>
                      <button 
                        onClick={() => {
                          setEditingFeature(null);
                          setImageFile(null);
                        }}
                        className="bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex justify-center mb-4">
                      <img 
                        src={imageUrl} 
                        alt={`${title} Icon`} 
                        className="h-16 w-16 object-contain"
                        onError={(e) => {
                          const imgEl = e.target as HTMLImageElement;
                          imgEl.src = feature.defaultImage;
                          console.log(`Error loading image for ${title}, using fallback`);
                        }}
                      />
                    </div>
                    <h3 className="font-semibold mb-2 text-center">{title}</h3>
                    <p className="text-gray-600 text-center">{description}</p>
                  </>
                )}
              </div>
            );
          })}
        </div>

        <div className="mt-10">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Public preview</h3>
          <FeatureShowcase
            title={featureSectionTitle}
            features={previewFeatures}
            layout={featureLayout}
            className="bg-white border border-gray-200 rounded-lg p-4"
          />
        </div>
      </>
    );
  };

  // Comprehensive loading state
  if (authLoading || loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  // Comprehensive error handling
  if (error || !trader) {
    return (
      <div className="flex justify-center items-center h-screen flex-col p-4 text-center">
        <div className="text-red-500 text-xl mb-4">
          {error || 'Unable to load trader data'}
        </div>
        <button 
          onClick={() => navigate('/trader/register')}
          className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 mb-4"
        >
          Go to Registration
        </button>
        {/* Detailed debug information */}
        <details className="w-full max-w-md bg-gray-100 rounded p-4 text-left">
          <summary className="cursor-pointer text-gray-700">
            Debug Information
          </summary>
          <pre className="text-xs overflow-x-auto">
            {JSON.stringify(debugInfo, null, 2)}
          </pre>
        </details>
      </div>
    );
  }

  // Ensure trader is defined before rendering
  if (!trader) {
    console.error('Unexpected: trader is undefined');
    return null;
  }

  // Compute map URL for dashboard preview based on trader location and visibility flag
  const hasLocationForMap = Boolean(trader.location && trader.location.trim().length > 0);
  const shouldShowMap = (trader as any).show_map !== false;
  const mapUrl = hasLocationForMap && shouldShowMap
    ? `https://www.google.com/maps?q=${encodeURIComponent(`${trader.location}, Ireland`)}&output=embed`
    : null;

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-center mb-4">
        <Link to="/">
          <img 
            src="/bally-Market-sign-200px.png" 
            alt="Ballyconnell Market Logo" 
            className="h-12 w-auto cursor-pointer"
          />
        </Link>
      </div>
      <h1 className="text-2xl font-bold mb-6">Trader Dashboard</h1>
      <div className="bg-white rounded-lg shadow p-6">
        <div className="mb-6 flex flex-col sm:flex-row items-center sm:items-center space-y-4 sm:space-y-0 sm:space-x-4">
          {trader.avatar_url ? (
            <div className="relative w-20 h-20 group">
              <img 
                src={trader.avatar_url}
                alt={`${trader.store_name} avatar`} 
                className="w-20 h-20 rounded-full object-cover border-4 border-blue-50 shadow-md"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(
                    trader.store_name
                  )}&background=random`
                }}
              />
              <AvatarUpload 
                trader={trader} 
                onAvatarUpdate={(newAvatarUrl) => {
                  setTrader(prev => prev ? { ...prev, avatar_url: newAvatarUrl } : null);
                }} 
              />
            </div>
          ) : (
            <div className="w-20 h-20 rounded-full bg-gray-200 flex items-center justify-center text-gray-500">
              <img 
                src={`https://ui-avatars.com/api/?name=${encodeURIComponent(
                  trader.store_name
                )}&background=random`}
                alt={`${trader.store_name} avatar`}
                className="w-full h-full rounded-full object-cover"
              />
            </div>
          )}
          <div className="text-center sm:text-left">
            <h2 className="text-lg font-semibold mb-1">
              Welcome, {trader.store_name || user?.user_metadata?.name || 'Trader'}
            </h2>
            <p className="text-gray-600">Manage your products and orders here.</p>
          </div>
        </div>

        <div className="mb-6 bg-gray-50 p-4 rounded-lg">
          <h3 className="text-md font-semibold mb-3">Your Business Details</h3>
          <div className="space-y-2">
            {/* Business Name */}
            <EditableField 
              label="Business Name"
              value={trader.store_name}
              icon={Store}
              onEdit={() => setEditingField('store_name')}
              isEditing={editingField === 'store_name'}
              onSave={(value) => {
                traderService.updateTrader(trader.id, { store_name: value })
                  .then(updatedTrader => {
                    if (updatedTrader) {
                      setTrader(updatedTrader);
                      setEditingField(null);
                    } else {
                      console.error('Failed to update business name: No data returned');
                      alert('Failed to update business name');
                    }
                  })
                  .catch(error => {
                    console.error('Failed to update business name', error);
                    alert('Failed to update business name');
                  });
              }}
              onCancel={() => setEditingField(null)}
              placeholder="Enter business name"
            />

            {/* Owner Name */}
            <EditableField 
              label="Owner Name"
              value={trader.owner_name}
              icon={User}
              onEdit={() => setEditingField('owner_name')}
              isEditing={editingField === 'owner_name'}
              onSave={(value) => {
                traderService.updateTrader(trader.id, { owner_name: value })
                  .then(updatedTrader => {
                    if (updatedTrader) {
                      setTrader(updatedTrader);
                      setEditingField(null);
                    } else {
                      console.error('Failed to update owner name: No data returned');
                      alert('Failed to update owner name');
                    }
                  })
                  .catch(error => {
                    console.error('Failed to update owner name', error);
                    alert('Failed to update owner name');
                  });
              }}
              onCancel={() => setEditingField(null)}
              placeholder="Enter owner name"
            />

            {/* Existing fields remain the same */}
            {trader.phone_number && (
              <EditableField 
                label="Phone Number"
                value={trader.phone_number}
                icon={Phone}
                onEdit={() => setEditingField('phone_number')}
                isEditing={editingField === 'phone_number'}
                onSave={(value) => {
                  traderService.updateTrader(trader.id, { phone_number: value })
                    .then(updatedTrader => {
                      if (updatedTrader) {
                        setTrader(updatedTrader);
                        setEditingField(null);
                      } else {
                        console.error('Failed to update phone number: No data returned');
                        alert('Failed to update phone number');
                      }
                    })
                    .catch(error => {
                      console.error('Failed to update phone number', error);
                      alert('Failed to update phone number');
                    });
                }}
                onCancel={() => setEditingField(null)}
                type="tel"
                placeholder="Enter phone number"
              />
            )}
            <EditableField 
              label="Website"
              value={trader.website || 'No website added'}
              icon={Globe}
              onEdit={() => setEditingField('website')}
              isEditing={editingField === 'website'}
              onSave={(value) => {
                traderService.updateTrader(trader.id, { website: value })
                  .then(updatedTrader => {
                    if (updatedTrader) {
                      setTrader(updatedTrader);
                      setEditingField(null);
                    } else {
                      console.error('Failed to update website: No data returned');
                      alert('Failed to update website');
                    }
                  })
                  .catch(error => {
                    console.error('Failed to update website', error);
                    alert('Failed to update website');
                  });
              }}
              onCancel={() => setEditingField(null)}
              type="url"
              placeholder="https://your-website.com"
            />
            {trader.location && (
              <EditableField 
                label="Location"
                value={trader.location}
                icon={MapPin}
                onEdit={() => setEditingField('location')}
                isEditing={editingField === 'location'}
                onSave={(value) => {
                  traderService.updateTrader(trader.id, { location: value })
                    .then(updatedTrader => {
                      if (updatedTrader) {
                        setTrader(updatedTrader);
                        setEditingField(null);
                      } else {
                        console.error('Failed to update location: No data returned');
                        alert('Failed to update location');
                      }
                    })
                    .catch(error => {
                      console.error('Failed to update location', error);
                      alert('Failed to update location');
                    });
                }}
                onCancel={() => setEditingField(null)}
                placeholder="Enter location"
              />
            )}
            {trader.description && (
              <EditableField 
                label="Description"
                value={trader.description}
                icon={Info}
                onEdit={() => setEditingField('description')}
                isEditing={editingField === 'description'}
                onSave={(value) => {
                  console.log('📝 Description update requested:', {
                    traderId: trader.id,
                    newValue: value,
                    valueLength: value.length,
                    currentDescription: trader.description
                  });
                  
                  traderService.updateTrader(trader.id, { description: value })
                    .then(updatedTrader => {
                      console.log('📥 Update response:', updatedTrader);
                      if (updatedTrader) {
                        setTrader(updatedTrader);
                        setEditingField(null);
                        alert('Description updated successfully!');
                      } else {
                        console.error('❌ Failed to update description: No data returned');
                        alert('Failed to update description: No data returned from server');
                      }
                    })
                    .catch(error => {
                      console.error('❌ Failed to update description - Error:', error);
                      console.error('Error type:', typeof error);
                      console.error('Error keys:', Object.keys(error));
                      alert(`Failed to update description: ${error.message || error}`);
                    });
                }}
                onCancel={() => setEditingField(null)}
                placeholder="Enter description"
                isTextarea={true}
              />
            )}
            
            {/* Regenerate Keywords Button */}
            <div className="mt-4 pt-4 border-t border-gray-200">
              <button
                onClick={async () => {
                  if (!trader) return;
                  
                  setIsRegeneratingKeywords(true);
                  try {
                    const success = await triggerKeywordGeneration(
                      trader.id,
                      'trader',
                      {
                        store_name: trader.store_name,
                        description: trader.description,
                        location: trader.location
                      }
                    );
                    
                    if (success) {
                      alert('✅ Keyword generation started! Keywords will be updated in a few moments.');
                    } else {
                      alert('⚠️ Failed to trigger keyword generation. Please try again.');
                    }
                  } catch (error) {
                    console.error('Error triggering keyword generation:', error);
                    alert('❌ Error triggering keyword generation');
                  } finally {
                    setIsRegeneratingKeywords(false);
                  }
                }}
                disabled={isRegeneratingKeywords}
                className={`
                  flex items-center justify-center gap-2 w-full px-4 py-2 
                  bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg
                  hover:from-blue-600 hover:to-blue-700
                  disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed
                  transition-all duration-200 shadow-md hover:shadow-lg
                  ${isRegeneratingKeywords ? 'opacity-75' : ''}
                `}
              >
                <RefreshCw className={`w-4 h-4 ${isRegeneratingKeywords ? 'animate-spin' : ''}`} />
                <span>
                  {isRegeneratingKeywords ? 'Generating Keywords...' : 'Regenerate Search Keywords'}
                </span>
              </button>
              <p className="text-xs text-gray-500 mt-2 text-center">
                Click to update search keywords based on your current business information
              </p>
            </div>
          </div>
        </div>

        <div className="mb-6">
          <SocialLinksCard />
        </div>
        
        <div className="mb-6 bg-gray-50 p-4 rounded-lg">
          <h3 className="text-md font-semibold mb-3">Location Map</h3>
          <div className="flex items-center justify-between text-sm text-gray-600 mb-3">
            <span>Show map on your public profile</span>
            <button
              type="button"
              onClick={handleToggleMapVisibility}
              className={`px-3 py-1 rounded-full border text-xs font-medium ${
                ((trader as any).show_map ?? true)
                  ? 'bg-green-50 text-green-700 border-green-200'
                  : 'bg-gray-50 text-gray-500 border-gray-200'
              }`}
              disabled={!hasLocationForMap}
            >
              {((trader as any).show_map ?? true) ? 'On' : 'Off'}
            </button>
          </div>
          {hasLocationForMap && mapUrl && (
            <div className="w-full overflow-hidden rounded-lg border border-gray-200">
              <iframe
                title={`Map preview for ${trader.store_name}`}
                src={mapUrl}
                width="100%"
                height="260"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          )}
          {hasLocationForMap && !mapUrl && (
            <p className="mt-2 text-xs text-gray-500">
              Map is currently hidden on your public profile because visibility is turned off above.
            </p>
          )}
          {!hasLocationForMap && (
            <p className="mt-2 text-xs text-gray-500">
              Add your business location above to enable the map preview on your public profile.
            </p>
          )}
        </div>

        {renderTraderFeatures()}
      </div>
      <Outlet />
    </div>
  );
};

export default TraderDashboard;
